import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    Button,
    confirm,
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogFooterSecondary,
    DialogHeader,
    FieldError,
    Form,
    Heading,
    Label,
    Submit,
    TextField,
    TextInput,
    toast,
    useDialogAction,
    useDialogClose,
} from '@gitbook/spine-react';

import { apiClient, useSiteContext } from '../../../../../state';
import type { McpServerFormInputs } from './AddMcpServerDialog';
import { McpServerHeadersField } from './McpServerHeadersField';

export function EditMcpServerDialog(props: { server: api.SiteMcpServer }) {
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Edit MCP server</Heading>
            </DialogHeader>
            <EditMcpServerForm server={props.server} />
        </Dialog>
    );
}

function EditMcpServerForm(props: { server: api.SiteMcpServer }) {
    const { organizationId, site } = useSiteContext();
    const [isPending, startDialogAction] = useDialogAction();
    const close = useDialogClose();

    const form = useForm<McpServerFormInputs>({
        defaultValues: {
            name: props.server.name,
            url: props.server.url,
            headers: Object.entries(props.server.headers).map(([key, value]) => ({ key, value })),
        },
    });

    const onSubmit: SubmitHandler<McpServerFormInputs> = async (data) => {
        await startDialogAction(async ({ close: closeDialog }) => {
            await toast.catch(async () => {
                const headers: Record<string, string> = {};
                data.headers.forEach((h) => {
                    if (h.key) headers[h.key] = h.value;
                });
                await apiClient.siteMcpServers.updateSiteMcpServerById(
                    organizationId,
                    site.id,
                    props.server.id,
                    {
                        name: data.name,
                        url: data.url,
                        headers,
                    }
                );
                analytics.logEvent(
                    analytics.createSiteEvent(
                        { id: site.id, organization: organizationId },
                        {
                            type: 'site_edit_mcp_server',
                        }
                    )
                );
                toast.success('MCP server saved');
                await closeDialog();
            }, 'Error while saving MCP server');
        });
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody className="flex flex-col gap-4">
                <TextField name="name" required>
                    <Label>Name</Label>
                    <TextInput autoFocus disabled={isPending} />
                    <Description>
                        The name of the MCP server. This will not be visible to your users.
                    </Description>
                    <FieldError />
                </TextField>
                <TextField name="url" required>
                    <Label>MCP Server URL</Label>
                    <TextInput placeholder="https://" disabled={isPending} />
                    <FieldError />
                </TextField>
                <McpServerHeadersField name="headers" disabled={isPending} />
            </DialogBody>
            <DialogFooter>
                <DialogFooterSecondary>
                    <Button
                        label="Delete"
                        kind="danger"
                        onPress={async () => {
                            await confirm({
                                kind: 'danger',
                                title: 'Delete MCP server',
                                confirmLabel: 'Delete',
                                description: 'Deleting this MCP server will remove it permanently.',
                                onConfirm: async () => {
                                    await toast.catch(async () => {
                                        await apiClient.siteMcpServers.deleteSiteMcpServerById(
                                            organizationId,
                                            site.id,
                                            props.server.id
                                        );
                                        analytics.logEvent(
                                            analytics.createSiteEvent(
                                                {
                                                    id: site.id,
                                                    organization: organizationId,
                                                },
                                                {
                                                    type: 'site_delete_mcp_server',
                                                }
                                            )
                                        );
                                        toast.success('MCP server deleted');
                                        close();
                                    }, 'Error while deleting MCP server');
                                },
                            });
                        }}
                        disabled={isPending}
                    />
                </DialogFooterSecondary>
                <DialogCloseButton />
                <Submit label="Save" />
            </DialogFooter>
        </Form>
    );
}
